import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type SemeiaSessionService from 'parog-web/services/session';
import { on } from '@ember/modifier';
import or from 'ember-truth-helpers/helpers/or';
import not from 'ember-truth-helpers/helpers/not';
import { fn } from '@ember/helper';

interface Signature {
  Args: {
    messageContent?: string;
    isMessageBeingSent: boolean;
    onSendMessage: () => void;
    onFileUploadClicked: () => void;
    onMedicalEventClicked: () => void;
    onSuggestionSelected: (suggestion: string) => void;
  };
}

export default class ChatSuggestionsComponent extends Component<Signature> {
  @service declare session: SemeiaSessionService;

  @tracked shouldShowSuggestions = false;

  buttonStyle =
    'flex justify-center items-baseline py-[1em] [&button]:mb-[0.5em]';

  onSuggestionSelected = (selectedSuggestion: string): void => {
    this.args.onSuggestionSelected(selectedSuggestion);

    this.shouldShowSuggestions = false;
  };

  showSuggestions = (): void => {
    this.shouldShowSuggestions = !this.shouldShowSuggestions;

    if (this.shouldShowSuggestions) {
      // If suggestions are shown, scroll to bottom to see them
      setTimeout(() => window.scrollTo(0, document.body.scrollHeight), 0);
    }
  };

  <template>
    {{#if this.session.medic}}
      <div class={{this.buttonStyle}}>
        <button
          type='button'
          class='ui small primary circular icon button'
          disabled={{@isMessageBeingSent}}
          data-test-chat='upload-file'
          {{on 'click' @onFileUploadClicked}}
        >
          <i class='paperclip icon'></i>
        </button>

        <button
          type='button'
          class='ui small primary circular icon
            {{if @isMessageBeingSent "loading"}}
            button'
          disabled={{@isMessageBeingSent}}
          data-test-chat='send-button'
          {{on 'click' @onSendMessage}}
        >
          <i class='arrow up icon'></i>
        </button>
      </div>
    {{else}}
      <div class={{this.buttonStyle}}>
        <button
          type='button'
          class='ui small primary circular icon button'
          disabled={{@isMessageBeingSent}}
          data-test-chat='upload-file'
          {{on 'click' @onFileUploadClicked}}
        >
          <i class='paperclip icon'></i>
        </button>
        <button
          type='button'
          class='ui small primary circular icon button'
          disabled={{@isMessageBeingSent}}
          data-test-chat='medical-event'
          {{on 'click' @onMedicalEventClicked}}
        >
          <i class='star icon outline'></i>
        </button>
        <button
          type='button'
          class='ui small primary circular icon button
            {{if
              this.shouldShowSuggestions
              "border-1 border-solid border-[#fc1369] bg-white [&_i]:text-[#fc1369]"
            }}'
          disabled={{@isMessageBeingSent}}
          data-test-chat='prescription'
          {{on 'click' this.showSuggestions}}
        >
          <i class='pills icon'></i>
        </button>

        <button
          type='button'
          class='ui small primary circular icon
            {{if @isMessageBeingSent "loading"}}
            button'
          disabled={{or @isMessageBeingSent (not @messageContent)}}
          data-test-chat='send-button'
          {{on 'click' @onSendMessage}}
        >
          <i class='arrow up icon'></i>
        </button>
      </div>
      {{#if this.shouldShowSuggestions}}
        <div
          class='pb-[1em] [&_.ui.button]:!ml-0 [&_.ui.button]:mb-[0.5em] [&_.ui.button]:flex [&_.ui.button]:w-full [&_.ui.button]:justify-between [&_.ui.button]:px-[1em] [&_.ui.button]:py-[0.78571429em]'
        >
          <button
            type='button'
            class='ui basic button'
            data-test-chat='medical-prescription'
            {{on 'click' (fn this.onSuggestionSelected 'medical-prescription')}}
          >
            Ordonnance médicale
            <i class='primary pills fitted icon'></i>
          </button>
          <button
            type='button'
            class='ui basic button'
            data-test-chat='exam-prescription'
            {{on 'click' (fn this.onSuggestionSelected 'exam-prescription')}}
          >
            Ordonnance d'examens médicaux
            <i class='primary clinic medical fitted icon'></i>
          </button>
          <button
            type='button'
            class='ui basic button'
            data-test-chat='bio-prescription'
            {{on 'click' (fn this.onSuggestionSelected 'bio-prescription')}}
          >
            Ordonnance de biologie
            <i class='primary syringe fitted icon'></i>
          </button>
        </div>
      {{/if}}
    {{/if}}
  </template>
}
